/**
 * Performance › Field data (`/performance/field`) — the standalone
 * Chrome UX Report: real Chrome users worldwide over a rolling
 * 28-day window (p75). CrUX is field data like the pixel but from
 * Google's public Chrome dataset, so it gets its own route rather
 * than diluting the pixel RUM view.
 *
 * Promoted from the old `/performance?view=field` tab to its own
 * route (2026-07-26) so the sidebar section panel can list it; that
 * legacy URL still redirects here via `/performance`'s `beforeLoad`.
 */
import { SectionBoundary } from '@/components/primitives/SectionBoundary';
import { CruxEmptyReport, CruxSection } from '@/components/rum/CruxSection';
import { CWV_METRIC_KEYS, type MetricKey } from '@/components/rum/PerformanceSection';
import { SiteIsland } from '@/components/shell/SiteIsland';
import { useActiveSiteUuid } from '@/lib/active-site';
import { useSiteCrux, useSiteCruxPages } from '@/lib/queries/crux';
import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

interface FieldSearch {
  /** Selected vital. In the URL so a metric view is shareable and the
   *  Overview's summary tiles can deep-link into one. */
  metric?: MetricKey;
}

const isMetricKey = (value: unknown): value is MetricKey =>
  CWV_METRIC_KEYS.includes(value as MetricKey);

export const Route = createFileRoute('/_app/performance/field')({
  validateSearch: (search: Record<string, unknown>): FieldSearch => ({
    ...(isMetricKey(search.metric) ? { metric: search.metric } : {}),
  }),
  component: FieldDataPage,
});

function FieldDataPage(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  return (
    <SiteIsland section="Field data" parent={{ label: 'Performance', href: '/performance' }}>
      {siteUuid && <FieldDataBody siteUuid={siteUuid} />}
    </SiteIsland>
  );
}

function FieldDataBody({ siteUuid }: { siteUuid: string }): ReactElement {
  const { metric } = Route.useSearch();
  const navigate = Route.useNavigate();

  // Re-checked at the point of use, not just `??`-defaulted — a parent
  // route's untouched copy of a bogus `?metric=` can arrive typed as a
  // valid key, and the charts index the metric registry with a `!`.
  const activeMetric: MetricKey = isMetricKey(metric) ? metric : 'lcp';
  const selectMetric = (next: MetricKey): void => {
    void navigate({ search: { metric: next }, replace: true });
  };

  // ALL series is the primary data; per-device series power the
  // device split, and the per-page table has its own pipe.
  const cruxAll = useSiteCrux(siteUuid, { formFactor: 'ALL' });
  const cruxPhone = useSiteCrux(siteUuid, { formFactor: 'PHONE' });
  const cruxDesktop = useSiteCrux(siteUuid, { formFactor: 'DESKTOP' });
  const cruxPages = useSiteCruxPages(siteUuid);

  return (
    <SectionBoundary label="this view">
      {cruxAll.isLoading ? (
        <CruxEmptyReport />
      ) : (
        <CruxSection
          data={cruxAll.data}
          variant="full"
          phone={cruxPhone.data}
          desktop={cruxDesktop.data}
          pages={cruxPages.data}
          siteUuid={siteUuid}
          metric={activeMetric}
          onMetricChange={selectMetric}
        />
      )}
    </SectionBoundary>
  );
}
